import { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, ChevronLeft, Trash2, Headset, ImagePlus, Megaphone, ClipboardList } from "lucide-react";
import api, { API, dlToken } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

export default function MonterChat({ user, onClose }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [sending, setSending] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [attachFor, setAttachFor] = useState(null);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const token = dlToken();
  const fileUrl = (id) => `${API}/files/${id}?auth=${token}`;

  const loadThreads = useCallback(() => api.get("/chat/threads").then((r) => setThreads(r.data || [])).catch(() => {}), []);
  useEffect(() => { loadThreads(); const id = setInterval(loadThreads, 8000); return () => clearInterval(id); }, [loadThreads]);
  const loadMsgs = useCallback((tid) => api.get(`/chat/messages?thread_id=${tid}`).then((r) => setMsgs(r.data || [])).then(() => loadThreads()).catch(() => {}), [loadThreads]);
  useEffect(() => { if (!active) return; loadMsgs(active); const id = setInterval(() => loadMsgs(active), 4000); return () => clearInterval(id); }, [active, loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if ((!text.trim() && !img) || !active || sending) return;
    setSending(true);
    try {
      let image_file_id;
      if (img) {
        const c = await compressImage(img);
        const fd = new FormData(); fd.append("file", c);
        const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_file_id = data.file_id;
      }
      await api.post("/chat/messages", { thread_id: active, text: text.trim(), image_file_id });
      setText(""); setImg(null); loadMsgs(active);
    }
    catch { toast.error("Nie udało się wysłać wiadomości"); }
    finally { setSending(false); }
  };
  const openAttach = async (fileId) => {
    setAttachFor(fileId);
    try { const { data } = await api.get("/schedule/tasks"); setTasks((data || []).filter((t) => t.status !== "gotowe")); }
    catch { setTasks([]); }
  };
  const doAttach = async (taskId) => {
    try { await api.post("/chat/attach-to-task", { file_id: attachFor, task_id: taskId }); toast.success("Przypięto zdjęcie do zlecenia"); setAttachFor(null); }
    catch { toast.error("Nie udało się przypiąć zdjęcia"); }
  };
  const startSupport = async () => {
    try { const { data } = await api.post("/chat/support"); await loadThreads(); setActive(data.id); }
    catch { toast.error("Nie udało się rozpocząć rozmowy z biurem"); }
  };
  const del = async (id) => {
    if (!window.confirm("Usunąć wiadomość? Kopia pozostaje na serwerze.")) return;
    try { await api.delete(`/chat/messages/${id}`); loadMsgs(active); }
    catch { toast.error("Nie udało się usunąć wiadomości"); }
  };
  const at = threads.find((x) => x.id === active);

  return (
    <div className="fixed inset-0 z-[2000] bg-[#0F172A] text-slate-100 flex flex-col" data-testid="monter-chat">
      <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-3 py-3 flex items-center gap-2">
        {active && <button onClick={() => setActive(null)} data-testid="monter-chat-back" className="text-slate-300"><ChevronLeft size={22} /></button>}
        <div className="flex-1 font-semibold truncate">{active ? (at?.name || "Rozmowa") : "Wiadomości"}</div>
        <button onClick={onClose} data-testid="monter-chat-close" className="text-slate-300"><X size={22} /></button>
      </div>
      {!active ? (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          <button onClick={startSupport} data-testid="monter-chat-support"
            className="w-full flex items-center justify-center gap-2 bg-cyan-600 hover:bg-cyan-500 rounded-xl px-3 py-3 text-sm font-semibold">
            <Headset size={18} /> Napisz do biura (infolinia)
          </button>
          {threads.length === 0 && <div className="text-slate-500 text-sm text-center py-8">Brak rozmów. Napisz do biura powyżej albo poczekaj, aż biuro rozpocznie czat.</div>}
          {threads.map((th) => (
            <button key={th.id} onClick={() => setActive(th.id)} data-testid={`monter-chat-thread-${th.id}`}
              className="w-full text-left bg-slate-800/60 border border-slate-700 rounded-xl p-3 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-1.5">{th.kind === "broadcast" && <Megaphone size={14} className="text-amber-400 shrink-0" />}{th.name}</div>
                <div className="text-xs text-slate-400 truncate">{th.last_message || "—"}</div>
              </div>
              {th.unread > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{th.unread}</span>}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.map((m) => (
              <div key={m.id} className={`group max-w-[80%] rounded-2xl px-3 py-2 text-sm relative ${m.sender_id === user.id ? "ml-auto bg-cyan-600 text-white" : "bg-slate-800 border border-slate-700"}`}>
                {m.sender_id !== user.id && <div className="text-[10px] text-cyan-300 mb-0.5">{m.sender_name}</div>}
                {m.deleted ? <div className="italic opacity-60">Wiadomość usunięta</div>
                  : <>
                      {m.image_file_id && <a href={fileUrl(m.image_file_id)} target="_blank" rel="noreferrer" data-testid={`monter-chat-img-${m.id}`}><img src={fileUrl(m.image_file_id)} alt="" className="rounded-lg max-w-full mb-1 max-h-72 object-cover" /></a>}
                      {m.image_file_id && m.sender_id === user.id && (
                        <button onClick={() => openAttach(m.image_file_id)} data-testid={`monter-chat-pin-${m.id}`}
                          className="mb-1 text-[11px] flex items-center gap-1 text-cyan-100/90 hover:text-white underline"><ClipboardList size={12} /> Przypnij do zlecenia</button>
                      )}
                      {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    </>}
                <div className="text-[9px] opacity-60 mt-0.5 text-right">{(m.ts || "").slice(11, 16)}</div>
                {!m.deleted && m.sender_id === user.id && (
                  <button onClick={() => del(m.id)} data-testid={`monter-chat-del-${m.id}`}
                    className="absolute -left-7 top-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t border-slate-800 pb-safe" data-testid="monter-chat-composer">
            {at?.kind === "broadcast" ? (
              <div className="text-center text-xs text-slate-500 py-2" data-testid="monter-chat-readonly">📢 Kanał ogłoszeń biura — tylko do odczytu</div>
            ) : (
              <>
                {img && <div className="text-[11px] text-slate-400 flex items-center gap-2 mb-1.5 px-1">📷 {img.name} <button onClick={() => setImg(null)} data-testid="monter-chat-img-clear" className="text-red-400">✕</button></div>}
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setImg(e.target.files?.[0] || null)} data-testid="monter-chat-file-input" />
                  <button onClick={() => fileRef.current?.click()} data-testid="monter-chat-attach" className="bg-slate-800 rounded-full p-2.5 text-slate-300"><ImagePlus size={18} /></button>
                  <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Napisz…" data-testid="monter-chat-input" className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 text-sm outline-none" />
                  <button onClick={send} disabled={sending} data-testid="monter-chat-send" className="bg-cyan-600 disabled:opacity-50 rounded-full p-2.5"><Send size={18} /></button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {attachFor && (
        <div className="absolute inset-0 z-[2100] bg-black/60 flex items-end" data-testid="monter-attach-picker" onClick={() => setAttachFor(null)}>
          <div className="w-full bg-slate-900 border-t border-slate-700 rounded-t-2xl max-h-[70%] overflow-y-auto p-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold mb-2 flex items-center gap-2"><ClipboardList size={16} className="text-cyan-400" /> Przypnij zdjęcie do zlecenia</div>
            {tasks.length === 0 && <div className="text-slate-500 text-sm py-4 text-center">Brak aktywnych zleceń.</div>}
            {tasks.map((tk) => (
              <button key={tk.id} onClick={() => doAttach(tk.id)} data-testid={`monter-attach-task-${tk.id}`}
                className="w-full text-left bg-slate-800/60 border border-slate-700 rounded-xl p-3 mb-2 active:bg-slate-800">
                <div className="text-xs text-slate-400">{tk.task_no} · {tk.type_label}</div>
                <div className="font-medium truncate">{tk.title}</div>
                {tk.address && <div className="text-xs text-slate-500 truncate">{tk.address}</div>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
